import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import type { MeResponse } from '@ucp/web/lib';
import { isSessionExpired } from '../auth/session';

/**
 * Acquire an API access token via MSAL (silent, falling back to redirect) and
 * call `GET /me` to drive the access gate (PRO-0010). The `MeResponse` type is
 * shared with the API through `@ucp/web/lib` (type-only import → erased from the
 * browser bundle).
 */
export type CurrentUserState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'ready'; user: MeResponse }
  | { status: 'expired'; reason: 'timeout' | 'revoked'; reload: () => Promise<void> }
  | { status: 'error'; message: string };

// Dev goes through the Vite proxy (`/api` → API_PORT, rewritten to `/`); prod
// uses the configured API base URL.
const apiBaseUrl = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL ?? '');
const REVALIDATE_MS_DEFAULT = 300_000;
const FOCUS_REVALIDATE_DEBOUNCE_MS = 500;

function readRevalidateIntervalMs(): number {
  const raw = import.meta.env.VITE_AUTH_REVALIDATE_MS;
  if (raw === undefined || raw === null || raw === '') {
    return REVALIDATE_MS_DEFAULT;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return REVALIDATE_MS_DEFAULT;
  }

  return parsed;
}

export function useCurrentUser(): CurrentUserState {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [state, setState] = useState<CurrentUserState>({ status: 'loading' });
  const wasAuthorisedRef = useRef(false);
  const revalidateTimerRef = useRef<number | null>(null);
  const revalidateIntervalMs = useMemo(readRevalidateIntervalMs, []);

  const load = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;

    if (!account) {
      wasAuthorisedRef.current = false;
      setState({ status: 'unauthenticated' });
      return;
    }

    if (isSessionExpired(account)) {
      if (import.meta.env.DEV && account.idTokenClaims?.auth_time === undefined && account.idTokenClaims?.iat === undefined) {
        console.warn('[auth] session anchor claims missing; treating session as expired');
      }
      setState({ status: 'expired', reason: 'timeout', reload: () => load() });
      return;
    }

    // Stale-while-revalidate: keep the shell mounted on focus/interval refresh so
    // in-progress form state is not wiped (PRO-0530 UAT). Initial sign-in still
    // shows the loading gate.
    if (!background) {
      setState({ status: 'loading' });
    }
    try {
      let accessToken: string;
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account });
        accessToken = result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Interactive consent / re-auth required; redirect navigates away.
          await instance.acquireTokenRedirect({ ...loginRequest, account });
          return;
        }
        throw error;
      }

      const response = await fetch(`${apiBaseUrl}/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 401) {
        setState({ status: 'expired', reason: 'revoked', reload: () => load() });
        return;
      }
      if (response.status === 503) {
        setState({
          status: 'error',
          message: 'Sign-in is temporarily unavailable. Please try again shortly.',
        });
        return;
      }
      if (!response.ok) {
        setState({ status: 'error', message: `Unexpected error (HTTP ${response.status}).` });
        return;
      }

      const user = (await response.json()) as MeResponse;
      if (user.accessConfigured) {
        wasAuthorisedRef.current = true;
      }

      if (!user.accessConfigured && wasAuthorisedRef.current) {
        // PRO-1440 AC16 — flag-off (`workforceBaseline === null`) is Access-not-configured,
        // not a revoked session.
        if (user.workforceBaseline === null || user.workforceBaseline === undefined) {
          setState({ status: 'ready', user });
          return;
        }
        setState({ status: 'expired', reason: 'revoked', reload: () => load() });
        return;
      }

      setState({ status: 'ready', user });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not contact the platform.';
      setState({ status: 'error', message });
    }
  }, [account, instance]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!account) {
      return undefined;
    }

    const clearDebounceTimer = () => {
      if (revalidateTimerRef.current !== null) {
        window.clearTimeout(revalidateTimerRef.current);
        revalidateTimerRef.current = null;
      }
    };

    const scheduleReload = () => {
      clearDebounceTimer();
      revalidateTimerRef.current = window.setTimeout(() => {
        revalidateTimerRef.current = null;
        void load({ background: true });
      }, FOCUS_REVALIDATE_DEBOUNCE_MS);
    };

    const handleFocus = () => {
      scheduleReload();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleReload();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let intervalId: number | null = null;
    if (revalidateIntervalMs > 0) {
      intervalId = window.setInterval(() => {
        void load({ background: true });
      }, revalidateIntervalMs);
    }

    return () => {
      clearDebounceTimer();
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [account, load, revalidateIntervalMs]);

  return state;
}
